// Ispis obrasca "POTVRDA O PLAĆI" (HZZO, R-1; R1.doc V11, 13.11.2025.) u pdf.
//
// Stranica 1 je popunjena iz podataka (ctx), stranica 2 je službeni dio kojeg popunjava HZZO
// (točka 4, naputak, evidencijski list) - ispisuje se prazna, vjerno strukturi obrasca.
// Sve crno-bijelo, bez pozadinskih boja. Samo okviri i debljina slova.
//
// ctx = {
//   poslodavac: 'Naziv, adresa, OIB', brojObveze,
//   prezime, ime, datumRodenja, oib, mbo, drugiStup: 'da' | 'ne' | '',
//   razdobljeOd, razdobljeDo,                 // vec formatirani 'DD.MM.YYYY.'
//   redci: [{mjesec, placa, isplacena, satiPuno, satiDuze, satiOdsutnost, satiUkupno, satiPN, satiKalendar}],
//   ukupno: {placa, isplacena, satiPuno, satiDuze, satiOdsutnost, satiUkupno, satiPN, satiKalendar},
//   brutoPoSatu, netoPoSatu,
//   mjesto, datum, ovlastenaOsoba
// }

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'

// Broj iz unosa: "1.352,23", "1352,23" i "1352.23" daju isto; prazno ili nebroj daje null
export const num = (v) => {
  if (v === null || v === undefined || v === '') { return null }
  if (typeof v === 'number') { return isNaN(v) ? null : v }
  let t = String(v).trim().replace(/\s/g, '')
  if (t.indexOf(',') !== -1) {
    t = t.replace(/\./g, '').replace(',', '.')
  } else if (/^\d{1,3}(\.\d{3})+$/.test(t)) {
    t = t.replace(/\./g, '')
  }
  const n = parseFloat(t)
  return isNaN(n) ? null : n
}

// 1.234,56
export const eur = (v, decimala = 2) => {
  const n = num(v)
  if (n === null) { return '' }
  const dijelovi = n.toFixed(decimala).split('.')
  dijelovi[0] = dijelovi[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return dijelovi.join(',')
}

// sati: bez nepotrebnih nula (168, 22,5)
export const sati = (v) => {
  const n = num(v)
  if (n === null) { return '' }
  return String(Math.round(n * 100) / 100).replace('.', ',')
}

// u tijelu tablice nula se ne ispisuje (prazno polje), u retku Ukupno ispisuje se uvijek
const eurRetka = (v) => (num(v) ? eur(v) : '')
const satiRetka = (v) => (num(v) ? sati(v) : '')

const FONT = 8.5
const CRNA = '#000000'
const SIROKA = 535 // A4 595 - 2 * 30

const PAD = 2.5
const LINIJA = 0.6

// pdfmake dodaje padding celija i okomite linije povrh sirina iz `widths`, pa se omjeri
// skaliraju tako da zbroj sirina + paddinga + linija tocno stane u SIROKA.
const W = (omjeri) => {
  const n = omjeri.length
  const raspolozivo = SIROKA - (n + 1) * LINIJA - n * 2 * PAD
  const zbroj = omjeri.reduce((a, b) => a + b, 0)
  return omjeri.map(o => Math.floor((o / zbroj) * raspolozivo * 100) / 100)
}

const layoutOkvir = (debljina) => ({
  hLineWidth: () => debljina || LINIJA,
  vLineWidth: () => debljina || LINIJA,
  hLineColor: () => CRNA,
  vLineColor: () => CRNA,
  paddingLeft: () => PAD,
  paddingRight: () => PAD,
  paddingTop: () => 3,
  paddingBottom: () => 3
})

const bilj = (t) => ({ text: '(' + t + ')', fontSize: 6, bold: false })

// Izbor "da * / ne *": odabrano je podebljano i podcrtano, drugo prekrizeno
const daNe = (izbor) => {
  const odabrano = { bold: true, decoration: 'underline' }
  const odbaceno = { decoration: 'lineThrough' }
  const da = izbor === 'da' ? odabrano : (izbor === 'ne' ? odbaceno : {})
  const ne = izbor === 'ne' ? odabrano : (izbor === 'da' ? odbaceno : {})
  return [
    Object.assign({ text: 'da *' }, da),
    { text: '   —   ' },
    Object.assign({ text: 'ne *' }, ne)
  ]
}

const etiketa = (t, opc) => Object.assign({ text: t, fontSize: 7.5 }, opc || {})
const vrijednost = (t, opc) => Object.assign({ text: t || '', fontSize: 10, bold: true }, opc || {})

const podnozje = () => ({
  columns: [
    { width: '*', text: [{ text: 'HZZO-Direkcija, Zagreb', fontSize: 7 }, { text: '      * - potrebno označiti', fontSize: 7 }] },
    { width: 'auto', text: 'R1.doc V11, 13.11.2025.', fontSize: 7, alignment: 'right' }
  ],
  margin: [30, 8, 30, 0]
})

const stranica1 = (c) => {
  const redci = (c.redci || []).slice(0, 6)
  while (redci.length < 6) { redci.push({}) }
  const u = c.ukupno || {}

  const glava = (t, opc) => Object.assign({ text: t, fontSize: 7, alignment: 'center', margin: [0, 1, 0, 1] }, opc || {})
  const w = W([56, 62, 62, 56, 56, 62, 46, 62, 58])
  const ph = () => ({ text: '' })
  const zaglavljeTablice = [
    [
      { text: [{ text: 'Isplata' }, bilj('2'), { text: '\nizvršena za\nmj./god.' }], fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 6, 0, 0] },
      { text: [{ text: 'Plaća' }, bilj('3')], fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 10, 0, 0] },
      { text: [{ text: 'Isplaćena' }, bilj('4'), { text: '\nplaća' }], fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 6, 0, 0] },
      glava('Broj sati provedenih na radu u', { colSpan: 2 }), ph(),
      { text: [{ text: 'Odsutnost' }, bilj('5'), { text: '\ns rada s pravom\nna naknadu plaće\nprema ZOR-u' }], fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 2, 0, 0] },
      { text: 'Ukupno\nsati', fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 8, 0, 0] },
      { text: 'Sati privremene\nnesposobnosti\nna teret Zavoda', fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 5, 0, 0] },
      { text: [{ text: 'Sati prema' }, bilj('6'), { text: '\nkalendaru\nradnog vremena' }], fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 5, 0, 0] }
    ],
    [
      ph(), ph(), ph(),
      glava('punom\nradnom\nvremenu'), glava('dužem od\npunog radnog\nvremena'),
      ph(), ph(), ph(), ph()
    ],
    ['1.', '2.', '3.', '4.', '5.', '6.', '7.', '8.', '9.'].map(t => ({ text: t, fontSize: 7, alignment: 'center' }))
  ]
  const celijaB = (t, poravnanje) => ({ text: t, fontSize: FONT, alignment: poravnanje || 'right', margin: [0, 2, 0, 2] })
  const tijelo = redci.map(r => [
    celijaB(r.mjesec || '', 'center'),
    celijaB(eurRetka(r.placa)),
    celijaB(eurRetka(r.isplacena)),
    celijaB(satiRetka(r.satiPuno)),
    celijaB(satiRetka(r.satiDuze)),
    celijaB(satiRetka(r.satiOdsutnost)),
    celijaB(satiRetka(r.satiUkupno)),
    celijaB(satiRetka(r.satiPN)),
    celijaB(satiRetka(r.satiKalendar))
  ])
  const ukupnoRed = [
    { text: 'Ukupno:', fontSize: FONT, bold: true, margin: [0, 2, 0, 2] },
    { text: eur(u.placa), fontSize: FONT, bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { text: eur(u.isplacena), fontSize: FONT, bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { text: sati(u.satiPuno), fontSize: FONT, bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { text: sati(u.satiDuze), fontSize: FONT, bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { text: sati(u.satiOdsutnost), fontSize: FONT, bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { text: sati(u.satiUkupno), fontSize: FONT, bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { text: sati(u.satiPN), fontSize: FONT, bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { text: sati(u.satiKalendar), fontSize: FONT, bold: true, alignment: 'right', margin: [0, 2, 0, 2] }
  ]

  return [
    // poslodavac
    {
      table: {
        widths: ['*'],
        body: [[{
          stack: [etiketa('NAZIV POSLODAVCA'), vrijednost(c.poslodavac, { fontSize: 9, margin: [0, 3, 0, 8] })],
          margin: [0, 0, 0, 0]
        }]]
      },
      layout: layoutOkvir(0.8)
    },
    // naslov i broj obveze
    {
      columns: [
        { width: '*', text: '' },
        {
          width: 200,
          stack: [
            { text: 'POTVRDA', fontSize: 22, bold: true, alignment: 'center' },
            { text: 'o plaći', fontSize: 16, bold: true, alignment: 'center' }
          ]
        },
        {
          width: '*',
          stack: [{
            table: {
              widths: ['auto', '*'],
              body: [[
                { text: 'Broj obveze', fontSize: 8, margin: [0, 4, 0, 4] },
                { text: c.brojObveze || '', fontSize: 10, bold: true, margin: [0, 3, 0, 3] }
              ]]
            },
            layout: layoutOkvir(0.8),
            margin: [20, 12, 0, 0]
          }]
        }
      ],
      margin: [0, 10, 0, 10]
    },
    // 1. podaci o osiguraniku
    { text: '1. PODACI O OSIGURANIKU', bold: true, fontSize: 10, margin: [0, 0, 0, 3] },
    {
      table: {
        widths: [64, '*', 80, '*'],
        body: [
          [etiketa('Prezime:'), vrijednost(c.prezime), etiketa('Dan, mjesec, godina rođenja:'), vrijednost(c.datumRodenja)],
          [etiketa('Ime:'), vrijednost(c.ime), etiketa('OIB:'), vrijednost(c.oib)],
          [etiketa('MBO:'), vrijednost(c.mbo, { colSpan: 3 }), { text: '' }, { text: '' }]
        ]
      },
      layout: layoutOkvir(0.6)
    },
    {
      text: [
        { text: 'Osiguranik individualne kapitalizirane štednje - II. stup mirovinskog osiguranja' },
        { text: '          ' }
      ].concat(daNe(c.drugiStup)),
      fontSize: 8.5,
      margin: [0, 5, 0, 9]
    },
    // 2. podaci o iznosima place
    {
      text: [
        { text: '2. PODACI O IZNOSIMA PLAĆE KOJA JE OSIGURANIKU ISPLAĆENA U POSLJEDNJIH ŠEST MJESECI PRIJE MJESECA U KOJEM JE NASTUPIO SLUČAJ NA OSNOVI KOJEG SE STJEČE PRAVO NA NAKNADU PLAĆE', bold: true, fontSize: 9 }
      ],
      margin: [0, 0, 0, 4]
    },
    {
      text: [
        { text: 'tj. u razdoblju od ' },
        { text: c.razdobljeOd || '', bold: true },
        { text: ' do ' },
        { text: c.razdobljeDo || '', bold: true },
        { text: ' ' },
        bilj('1'),
        { text: ', a za mjesece:' }
      ],
      fontSize: 9,
      margin: [0, 0, 0, 5]
    },
    {
      table: {
        headerRows: 3,
        widths: w,
        body: zaglavljeTablice.concat(tijelo, [ukupnoRed])
      },
      layout: layoutOkvir(0.6)
    },
    // 3. osnovica
    {
      text: [
        { text: '3. Osnovicu čini prosječni iznos plaće isplaćene u posljednjih šest mjeseci, a koja iznosi ' },
        { text: c.brutoPoSatu === null || c.brutoPoSatu === undefined || c.brutoPoSatu === '' ? '____________' : eur(c.brutoPoSatu, 4), bold: true, decoration: 'underline' },
        { text: ' eura po satu - bruto' },
        bilj('7'),
        { text: ' odnosno ' },
        { text: c.netoPoSatu === null || c.netoPoSatu === undefined || c.netoPoSatu === '' ? '____________' : eur(c.netoPoSatu, 4), bold: true, decoration: 'underline' },
        { text: ' eura po satu - neto' },
        bilj('8'),
        { text: '.' }
      ],
      fontSize: 9.5,
      lineHeight: 1.3,
      margin: [0, 14, 0, 18]
    },
    // mjesto, datum
    {
      text: [
        { text: 'U ' },
        { text: c.mjesto || '____________', bold: true },
        { text: ', ' },
        { text: c.datum || '__.__.20__.', bold: true },
        { text: ' g.' }
      ],
      fontSize: 9.5,
      margin: [0, 0, 0, 26]
    },
    {
      columns: [
        { width: '*', text: '' },
        {
          width: 250,
          stack: [
            { text: c.ovlastenaOsoba || '', fontSize: 9.5, bold: true, alignment: 'center', margin: [0, 0, 0, 2] },
            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 250, y2: 0, lineWidth: 0.5, lineColor: CRNA }] },
            { text: 'Ime i prezime/OIB ovlaštene osobe', fontSize: 8, alignment: 'center', margin: [0, 2, 0, 0] }
          ]
        }
      ]
    }
  ]
}

// ---------------------------------------------------------------------------
// Stranica 2: popunjava Sluzba HZZO-a. Prazan sluzbeni dio.
// ---------------------------------------------------------------------------

const NAPUTCI = [
  'Upisuje se prvi dan prvog mjeseca i posljednji dan posljednjeg mjeseca s naznakom godine šestomjesečnog razdoblja.',
  'Upisuju se mjeseci s naznakom godine za koje je, u razdoblju pod 1., isplaćena plaća (isplata plaće u tekućem za prethodni odnosno u tekućem za tekući mjesec).',
  'Upisuje se iznos plaće koja je na osnovi ugovora o radu, a prema osnovama i mjerilima koji su utvrđeni općim aktom poslodavca o raspodjeli plaće obračunata radniku za rad u punom, skraćenom i produženom radnom vremenu, naknade plaće isplaćene tijekom privremene nesposobnosti odnosno spriječenosti za rad.',
  'Upisuje se iznos redovite mjesečne plaće umanjene za zakonom propisane doprinose i porez koji se plaćaju iz plaće radnika, isplaćene prema osnovama pod 3.',
  'Upisuje se broj sati za koje je radnik opravdano izbivao s radnog mjesta (plaćeni dopust, godišnji odmor, privremena nesposobnost za rad koja tereti sredstva poslodavca).',
  'Upisuju se sati prema kalendaru radnog vremena poslodavca za mjesec iz stupca 1.',
  'Upisuje se iznos koji se dobiva kada se zbroj ukupno obračunatih plaća podijeli s brojem sati za koje je ta plaća obračunata - stupac: (2) : stupac (7).',
  'Upisuje se iznos koji se dobiva kada se zbroj ukupno isplaćenih plaća podijeli s brojem sati za koje je ta plaća isplaćena - stupac (3) : stupac (7).',
  'Upisuje se iznos koji se dobije kad se ukupno isplaćene plaće u šestomjesečnom razdoblju (toč. 4.) podijele s brojem sati toga razdoblja, uzimajući za radni tjedan 40 sati.',
  'Upisuje se iznos koji se dobije zbrajanjem iznosa pod (8.) i iznosa pod (9.).',
  'Upisuje se iznos koji se dobije kad se isplaćena plaća ili pripadajuća plaća podijeli s brojem radnih sati za koje je, ili bi bila, ostvarena (članak 54. stavak 5. Zakona o obveznom zdravstvenom osiguranju).',
  'Upisuje se iznos koji se dobije kad se najniža osnovica osiguranja, umanjena za zakonom propisane doprinose i porez, važeća za mjesec koji prethodi mjesecu nastanka osiguranog slučaja, podijeli s brojem radnih sati toga mjeseca (članak 54. stavak 5. Zakona o obveznom zdravstvenom osiguranju).'
]

const stranica2 = (c) => {
  const ev = c.evidencija || {}
  const isplateEv = (ev.isplate || []).slice(0, 5)
  while (isplateEv.length < 5) { isplateEv.push({}) }
  const ue = ev.ukupno || {}
  // odabrana oznaka: podebljana, podcrtana i u uglatim zagradama
  const izbor = (t, odabrano) => (odabrano ? { text: '[' + t + ']', bold: true, decoration: 'underline' } : { text: t })
  const tocka = (broj, sadrzaj) => ({
    columns: [{ width: 22, text: broj, fontSize: 8.5, bold: true }, { width: '*', text: sadrzaj, fontSize: 8, lineHeight: 1.35 }],
    margin: [0, 3, 0, 3]
  })
  const oznake = [
    ['A0', 'Bolest'], ['A1', 'Transplantacija u korist druge osigurane osobe'], ['B0', 'Ozljeda na radu'], ['C0', 'Profesionalna bolest'],
    ['D1', 'Izolacija kliconoše'], ['D2', 'Izolacija zbog zaraze u okolini'], ['DR', 'Posljedica sudjelovanja u Domovinskom ratu'],
    ['E0', 'Pratnja'], ['F6', 'Do sedme godine djeteta'], ['F3', 'Od sedme do 18. godine djeteta'],
    ['F5', 'Od 18. godine djeteta i njega supružnika / životnog partnera'], ['G0', 'Komplikacije u vezi trudnoće i poroda'],
    ['H1', 'Rodiljni dopust']
  ]
  const oz = (t) => ({ text: t, fontSize: 6, alignment: 'center', margin: [0, 1, 0, 1] })
  const ozKod = (t) => ({ text: ev.oznaka === t ? '[' + t + ']' : t, fontSize: 8, bold: true, decoration: ev.oznaka === t ? 'underline' : undefined, alignment: 'center', margin: [0, 5, 0, 5] })
  const ph = () => ({ text: '' })
  const oznakeTablica = {
    table: {
      headerRows: 2,
      widths: W([32, 44, 34, 42, 34, 38, 46, 32, 36, 38, 40, 46, 34, 44]),
      body: [
        // grupni naslov: njega clana obitelji (F6, F3, F5)
        [oz(oznake[0][1]), oz(oznake[1][1]), oz(oznake[2][1]), oz(oznake[3][1]), oz(oznake[4][1]), oz(oznake[5][1]), oz(oznake[6][1]), oz(oznake[7][1]),
          Object.assign(oz('Njega člana obitelji'), { colSpan: 3 }), ph(), ph(),
          oz(oznake[11][1]), oz(oznake[12][1]), oz('Primjena članaka 139. i 141. Zakona')],
        [ph(), ph(), ph(), ph(), ph(), ph(), ph(), ph(),
          oz(oznake[8][1]), oz(oznake[9][1]), oz(oznake[10][1]), ph(), ph(), ph()],
        oznake.slice(0, 8).map(o => ozKod(o[0])).concat(
          oznake.slice(8, 11).map(o => ozKod(o[0])),
          oznake.slice(11).map(o => ozKod(o[0])),
          [{ text: daNe(ev.primjena139), fontSize: 7, alignment: 'center', margin: [0, 6, 0, 6] }]
        )
      ]
    },
    layout: layoutOkvir(0.5)
  }
  // 1. red tablice treba imati i zasebni red naslova za F-stupce; dva zaglavna reda spojena rowSpan-om
  oznakeTablica.table.body[0] = oznakeTablica.table.body[0].map((c, i) => (i === 8 || i === 9 || i === 10 || c.colSpan ? c : Object.assign({}, c, { rowSpan: 2 })))

  const isplate = W([60, 50, 50, 44, 38, 46, 56, 70, 95])
  const hd = (t, opc) => Object.assign({ text: t, fontSize: 6.5, alignment: 'center', margin: [0, 2, 0, 2] }, opc || {})
  const pc = (t, poravnanje) => ({ text: t === null || t === undefined || t === '' ? ' ' : String(t), fontSize: 8.5, alignment: poravnanje || 'right', margin: [0, 3, 0, 3] })
  const redIsplate = (r) => [
    pc(r.datum, 'center'), pc(r.od, 'center'), pc(r.do, 'center'),
    pc(r.dani === undefined || r.dani === '' ? '' : sati(r.dani)), pc(r.sati === undefined || r.sati === '' ? '' : sati(r.sati)),
    pc(r.posto === undefined || r.posto === '' ? '' : sati(r.posto)),
    pc(r.satni === undefined || r.satni === '' ? '' : eur(r.satni, 4)), pc(r.iznos === undefined || r.iznos === '' ? '' : eur(r.iznos)),
    { text: ' ', fontSize: 8.5, margin: [0, 3, 0, 3] }
  ]
  const isplateTablica = {
    table: {
      headerRows: 2,
      widths: isplate,
      body: [
        [hd('Datum\nisplate', { rowSpan: 2, margin: [0, 6, 0, 0] }), hd('Isplata izvršena za vrijeme', { colSpan: 4 }), ph(), ph(), ph(),
          hd('% od\nosnovice', { rowSpan: 2, margin: [0, 6, 0, 0] }), hd('Satni iznos\nnaknade', { rowSpan: 2, margin: [0, 6, 0, 0] }),
          hd('Iznos\neura', { rowSpan: 2, margin: [0, 6, 0, 0] }), hd('Kontrolirao\n(potpis)', { rowSpan: 2, margin: [0, 6, 0, 0] })],
        [ph(), hd('od'), hd('do'), hd('Broj dana'), hd('Broj\nsati'), ph(), ph(), ph(), ph()],
        ...isplateEv.map(redIsplate),
        [{ text: 'Ukupno:', fontSize: 8, bold: true, colSpan: 3, margin: [0, 3, 0, 3] }, ph(), ph(),
          { text: ue.dani ? sati(ue.dani) : ' ', fontSize: 8.5, bold: true, alignment: 'right', margin: [0, 3, 0, 3] },
          { text: ue.sati ? sati(ue.sati) : ' ', fontSize: 8.5, bold: true, alignment: 'right', margin: [0, 3, 0, 3] },
          { text: ' ' }, { text: ' ' },
          { text: ue.iznos ? eur(ue.iznos) : ' ', fontSize: 8.5, bold: true, alignment: 'right', margin: [0, 3, 0, 3] },
          { text: ' ' }]
      ]
    },
    layout: layoutOkvir(0.5)
  }

  const evid = {
    table: {
      widths: W([108, 76, 76, 140, 110]),
      body: [
        [
          { text: 'Datum početka privremene nesposobnosti po određenoj dijagnozi', fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 6, 0, 0] },
          { text: 'Datum privremene nesposobnosti', fontSize: 7, alignment: 'center', colSpan: 2 }, ph(),
          { text: 'Broj evidencije prijave ozljede/bolesti', fontSize: 7, alignment: 'center', rowSpan: 2, margin: [0, 6, 0, 0] },
          { text: [izbor('PN', ev.vrstaPN === 'PN'), { text: '        ' }, izbor('TJO', ev.vrstaPN === 'TJO')], fontSize: 8, alignment: 'center', rowSpan: 2, margin: [0, 8, 0, 0] }
        ],
        [ph(), { text: 'prvi', fontSize: 7, alignment: 'center' }, { text: 'posljednji', fontSize: 7, alignment: 'center' }, ph(), ph()],
        [{ text: ev.datumPocetka || ' ', fontSize: 9, bold: true, alignment: 'center', margin: [0, 3, 0, 3] }, { text: ev.prviDan || ' ', fontSize: 9, bold: true, alignment: 'center', margin: [0, 3, 0, 3] }, { text: ev.posljednjiDan || ' ', fontSize: 9, bold: true, alignment: 'center', margin: [0, 3, 0, 3] }, { text: ev.brojEvidencije || ' ', fontSize: 9, bold: true, alignment: 'center', margin: [0, 3, 0, 3] }, { text: ' ' }],
        [
          { text: 'Evidencijski broj priznate ozljede na radu /profesionalne bolesti', fontSize: 7, alignment: 'center', colSpan: 3, margin: [0, 3, 0, 3] }, ph(), ph(),
          { text: ev.brojOzljede || ' ', fontSize: 9, bold: true, alignment: 'center', margin: [0, 3, 0, 3] },
          { text: [izbor('OR', ev.vrstaOzljede === 'OR'), { text: '        ' }, izbor('PB', ev.vrstaOzljede === 'PB')], fontSize: 8, alignment: 'center', margin: [0, 3, 0, 3] }
        ]
      ]
    },
    layout: layoutOkvir(0.5)
  }

  return [
    {
      table: {
        widths: ['*'],
        body: [[{ text: 'POPUNJAVA SLUŽBA HRVATSKOG ZAVODA ZA ZDRAVSTVENO OSIGURANJE', bold: true, fontSize: 9, alignment: 'center', margin: [0, 2, 0, 2] }]]
      },
      layout: layoutOkvir(0.8),
      pageBreak: 'before'
    },
    tocka('4.', [
      { text: 'Podatak o ukupnom iznosu drugog dohotka prema zakonu kojim se uređuju obvezni doprinosi koji je osiguraniku isplaćen u posljednjih šest mjeseci prije mjeseca u kojem je nastupio slučaj na osnovi kojeg se stječe pravo na naknadu plaće tj. u razdoblju od ' },
      c.razdobljeOd ? { text: c.razdobljeOd, bold: true } : { text: '____________' },
      { text: ' do ' },
      c.razdobljeDo ? { text: c.razdobljeDo, bold: true } : { text: '____________' },
      { text: ' ' },
      bilj('1'), { text: ': Iznos od ____________ eura utvrđen na osnovi ukupno ______ potvrde/a isplatitelja primitaka, koje/u osiguranik prilaže uz ovu potvrdu.' }
    ]),
    tocka('4.1.', [{ text: 'Prosječni iznos primitaka u eurima po satu - neto: ' }, bilj('9'), { text: ' ____________ eura.' }]),
    tocka('4.2.', [{ text: 'Osnovicu čini prosječni iznos plaće i primitaka isplaćenih u posljednjih šest mjeseci a koja iznosi: ' }, bilj('10'), { text: ' ____________ eura po satu - neto.' }]),
    tocka('4.3.', [
      { text: 'Iznimno, kad se osnovica za naknadu plaće ne može utvrditi kao prosječna plaća (toč. 4.2.), osnovicu čini posljednja isplaćena ili pripadajuća plaća koja iznosi: ____________ eura po satu neto' },
      bilj('11'),
      { text: ', s tim da tako utvrđena osnovica ne može biti veća od najniže osnovice osiguranja za obvezno zdravstveno osiguranje koja iznosi: ____________ eura po satu neto' },
      bilj('12'), { text: '.' }
    ]),
    { text: 'NAPUTAK O NAČINU POPUNJAVANJA', bold: true, fontSize: 9, alignment: 'center', margin: [0, 8, 0, 3] },
    {
      text: 'Potvrda se popunjava u dva primjerka od kojih jedan zadržava poslodavac. Uz navedeni primjerak poslodavac je obvezan čuvati izvorne podatke na osnovi kojih je popunio ovu potvrdu, kako bi se mogla obaviti provjera podataka.',
      fontSize: 6.8, margin: [0, 0, 0, 2]
    },
    {
      table: {
        widths: [14, '*'],
        body: NAPUTCI.map((t, i) => [{ text: (i + 1) + '.', fontSize: 6.8, alignment: 'right', margin: [0, 0, 3, 0] }, { text: t, fontSize: 6.8 }])
      },
      layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0.8, paddingBottom: () => 0.8 }
    },
    { text: 'EVIDENCIJSKI LIST O IZVRŠENIM ISPLATAMA PO ZAKONU O OBVEZNOM ZDRAVSTVENOM OSIGURANJU', bold: true, fontSize: 8.5, alignment: 'center', margin: [0, 10, 0, 4] },
    evid,
    { text: '1. Evidencijske oznake uzroka privremene nesposobnosti / spriječenosti za rad', fontSize: 7.5, bold: true, margin: [0, 5, 0, 2] },
    oznakeTablica,
    {
      // izvedena vrijednost: isti iznos kao "eura po satu - neto" na stranici 1 (tocka 3)
      text: [
        { text: 'Satni iznos osnovice za određivanje naknade plaće iznosi ' },
        c.netoPoSatu === null || c.netoPoSatu === undefined || c.netoPoSatu === '' ? { text: '____________' } : { text: eur(c.netoPoSatu, 4), bold: true, decoration: 'underline' },
        { text: ' eura.' }
      ],
      fontSize: 8,
      margin: [0, 6, 0, 4]
    },
    isplateTablica,
    { text: 'Potpis ovlaštenog radnika Zavoda', fontSize: 8, alignment: 'right', margin: [0, 14, 0, 0] }
  ]
}

export const buildPotvrdaOPlaciPdf = (ctx) => ({
  pageSize: 'A4',
  pageOrientation: 'portrait',
  pageMargins: [30, 28, 30, 34],
  defaultStyle: { fontSize: 9 },
  content: stranica1(ctx).concat(stranica2(ctx)),
  footer: podnozje
})

export const printPotvrdaOPlaciPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildPotvrdaOPlaciPdf(ctx)).open()
}

export default { buildPotvrdaOPlaciPdf, printPotvrdaOPlaciPdf }
